import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDownIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { navigationCategories, NavigationItem } from '../../config/navigationConfig';

interface CollapsibleNavProps {
  onNavigate?: () => void;
}

export const CollapsibleNav: React.FC<CollapsibleNavProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    const savedExpanded = localStorage.getItem('myhealth-nav-expanded-categories');
    if (savedExpanded) {
      setExpandedCategories(new Set(JSON.parse(savedExpanded)));
    } else {
      const defaultExpanded = navigationCategories
        .filter(cat => cat.defaultExpanded)
        .map(cat => cat.id);
      setExpandedCategories(new Set(defaultExpanded));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('myhealth-nav-expanded-categories', JSON.stringify(Array.from(expandedCategories)));
  }, [expandedCategories]);

  useEffect(() => {
    const currentCategory = navigationCategories.find(cat =>
      cat.items.some(item => item.path === location.pathname)
    );
    if (currentCategory && !expandedCategories.has(currentCategory.id)) {
      setExpandedCategories(prev => new Set([...Array.from(prev), currentCategory.id]));
    }
  }, [location.pathname, expandedCategories]);

  const handleItemClick = (item: NavigationItem) => {
    if (!item.enabled) {
      return;
    }
    navigate(item.path);
    onNavigate?.();
  };

  const isActive = (item: NavigationItem) => {
    return location.pathname === item.path;
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  return (
    <nav className="space-y-1 px-2 py-4">
      {navigationCategories.map((category) => {
        const isExpanded = expandedCategories.has(category.id);
        const CategoryIcon = category.icon;

        return (
          <div key={category.id} className="mb-2">
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <CategoryIcon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
              <span className="flex-1 text-left">{t(category.labelKey)}</span>
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${
                  isExpanded ? 'transform rotate-180' : ''
                }`}
              />
            </button>

            {isExpanded && (
              <div className="mt-1 ml-8 space-y-1">
                {category.items.map((item) => {
                  const ItemIcon = item.icon;
                  const active = isActive(item);

                  return (
                    <div key={item.id} className="relative group">
                      <button
                        onClick={() => handleItemClick(item)}
                        disabled={!item.enabled}
                        className={`
                          w-full flex items-center px-3 py-2 text-sm rounded-md transition-all
                          ${active
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-medium'
                            : item.enabled
                            ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                          }
                        `}
                        title={item.enabled ? (item.descriptionKey ? t(item.descriptionKey) : '') : `${item.descriptionKey ? t(item.descriptionKey) : ''} - ${item.releaseQuarter}`}
                      >
                        <ItemIcon
                          className={`mr-3 h-5 w-5 flex-shrink-0 ${
                            active ? 'text-emerald-600 dark:text-emerald-400' : item.enabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                        <span className="flex-1 text-left truncate">{t(item.labelKey)}</span>

                        {item.badgeKey && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {t(item.badgeKey)}
                          </span>
                        )}
                        {!item.enabled && (
                          <LockClosedIcon className="ml-2 h-4 w-4 text-gray-300 dark:text-gray-600" />
                        )}
                      </button>

                      {!item.enabled && (
                        <div className="hidden group-hover:block absolute left-full ml-2 top-0 z-50 w-64 p-3 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg border border-gray-700">
                          <div className="font-semibold mb-1">{t(item.labelKey)}</div>
                          <div className="text-gray-300 mb-2">{item.descriptionKey ? t(item.descriptionKey) : ''}</div>
                          {item.releaseQuarter && (
                            <div className="text-emerald-300 dark:text-emerald-400 font-medium">
                              {item.releaseQuarter}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};
